import { useEffect } from "react"

import {
  Box,
  FormErrorMessage as ChakraFormErrorMessage,
  Input as ChakraInput,
  Flex,
} from "@chakra-ui/react"
import InputMask, { Props as InputMaskProps } from "react-input-mask"
import { DateFieldTypes, DateFormat } from "types"

import { ErrorIcon } from "components/ErrorIcon"

import { Checkbox } from "./Checkbox"

export interface DateInputMaskProps extends Omit<InputMaskProps, "mask"> {
  type: DateFieldTypes
  isDateInputDisabled?: boolean
  setIsDateInputDisabled?: (isDateInputDisabled: boolean) => void
  errorText?: string
}

// Create a wrapper component to handle the type issue
const InputMaskWrapper = InputMask as unknown as React.FC<InputMaskProps>

/**
 * TODO: Update to address console warning:
 * > Warning: findDOMNode is deprecated and will be removed in the next major release. Instead, add a ref directly to the element you want to reference. Learn more about using refs safely here: https://reactjs.org/link/strict-mode-find-node 
 */
const DateInputMask = ({
  type,
  onChange,
  disabled,
  isDateInputDisabled,
  errorText,
  setIsDateInputDisabled,
  value,
  onBlur,
  ...rest
}: DateInputMaskProps) => {
  const handleCheckboxClick = () => {
    const newDisabledState = !isDateInputDisabled

    if (!isDateInputDisabled) {
      // Clear the date value when checking the box
      const syntheticEvent = {
        target: { name: rest.name, value: "" },
      } as any
      onChange?.(syntheticEvent)
    }

    setIsDateInputDisabled?.(newDisabledState)

    // Trigger onBlur to save the form
    if (onBlur && typeof onBlur === "function") {
      const syntheticEvent = {
        target: { name: rest.name, value: newDisabledState ? "" : value },
      } as any
      onBlur(syntheticEvent)
    }
  }

  const dateFormat = DateFormat[type]

  const formatToMask = dateFormat.replace(/[A-Za-z]/g, "9")

  const isEndDate = rest.name?.includes("to_date")

  useEffect(() => {
    if (value && onBlur && typeof onBlur === "function") {
      // Fake a blur event to trigger validation
      const syntheticEvent = {
        target: { name: rest.name, value },
      } as any

      onBlur(syntheticEvent)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []) // Only needs to run when component mounts

  return (
    <Box w="100%">
      <InputMaskWrapper
        onChange={originalEvent => {
          const parsedEvent = originalEvent
          //  if there are no numbers the value, return empty
          if (!originalEvent.target.value.match(/\d/g)) {
            parsedEvent.target.value = ""
          }
          return onChange?.(parsedEvent)
        }}
        mask={formatToMask}
        alwaysShowMask={true}
        disabled={isDateInputDisabled || disabled}
        value={value || ""}
        onBlur={onBlur}
        {...rest}
      />
      {errorText && (
        <ChakraFormErrorMessage color="errorText">
          <ErrorIcon color="errorText" mr={1} w={4} h={4} /> {errorText}
        </ChakraFormErrorMessage>
      )}
      {isEndDate && (
        <Flex ml="2" mt="2">
          <Checkbox
            label="I'm currently attending this school"
            value=""
            isChecked={isDateInputDisabled}
            onChange={handleCheckboxClick}
            disabled={false}
            isInvalid={false}
          />
        </Flex>
      )}
    </Box>
  )
}

// NOTE: The findDOMNode warning from react-input-mask is a known issue
// with the library and can be safely ignored. This is caused by the internal
// implementation of the library and not by our code.
// See: https://github.com/sanniassin/react-input-mask/issues/239

export const DateInput = ({ ...rest }) => {
  return <ChakraInput as={DateInputMask} {...{ ...rest }} />
}
